import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Property } from '@/mocks/properties';
import { useCompare } from '@/context/CompareContext';

interface PropertyCardProps {
  property: Property;
  onSave?: (id: string) => void;
  saved?: boolean;
}

const PropertyCard = ({ property, onSave, saved = false }: PropertyCardProps) => {
  const [isSaved, setIsSaved] = useState(saved);
  const { addToCompare, removeFromCompare, isInCompare, canAdd } = useCompare();
  const inCompare = isInCompare(property.id);

  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSaved(!isSaved);
    onSave?.(property.id);
  };

  const handleCompare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (inCompare) {
      removeFromCompare(property.id);
    } else if (canAdd) {
      addToCompare(property);
    }
  };

  const formatPrice = (price: number, type: string) => {
    if (type === 'rent') return `$${price.toLocaleString()}/mo`;
    return `$${(price / 1000).toFixed(0)}K`;
  };

  return (
    <Link
      to={`/listings/${property.id}`}
      className="group block bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      data-product-shop
    >
      {/* Image */}
      <div className="relative w-full h-52 overflow-hidden">
        <img
          src={property.image}
          alt={property.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {property.isNew && (
            <span className="px-2.5 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full">New</span>
          )}
          {property.isFeatured && (
            <span className="px-2.5 py-1 bg-stone-800/80 text-white text-xs font-semibold rounded-full backdrop-blur-sm">Featured</span>
          )}
        </div>
        {/* Action Buttons */}
        <div className="absolute top-3 right-3 flex flex-col gap-1.5">
          <button
            onClick={handleSave}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors cursor-pointer"
            title="Save property"
          >
            <i className={`${isSaved ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-stone-500'} text-base`}></i>
          </button>
          <button
            onClick={handleCompare}
            disabled={!inCompare && !canAdd}
            className={`w-8 h-8 flex items-center justify-center rounded-full backdrop-blur-sm transition-colors cursor-pointer ${
              inCompare
                ? 'bg-amber-500 hover:bg-amber-400'
                : canAdd
                ? 'bg-white/90 hover:bg-white'
                : 'bg-white/50 cursor-not-allowed'
            }`}
            title={inCompare ? 'Remove from compare' : canAdd ? 'Add to compare' : 'Compare list full (max 3)'}
          >
            <i className={`ri-scales-line text-base ${inCompare ? 'text-white' : 'text-stone-500'}`}></i>
          </button>
        </div>
        {/* Price Tag */}
        <div className="absolute bottom-3 left-3">
          <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm text-stone-900 text-sm font-bold rounded-lg">
            {formatPrice(property.price, property.priceType)}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-semibold text-stone-800 text-sm leading-snug mb-1 line-clamp-2 group-hover:text-amber-600 transition-colors">
          {property.title}
        </h3>
        <p className="text-stone-400 text-xs flex items-center gap-1 mb-3">
          <i className="ri-map-pin-line"></i>
          {property.location}
        </p>

        {/* Specs */}
        <div className="flex items-center gap-3 text-stone-500 text-xs border-t border-stone-50 pt-3">
          <span className="flex items-center gap-1">
            <i className="ri-hotel-bed-line"></i> {property.bedrooms} Beds
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-drop-line"></i> {property.bathrooms} Baths
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-layout-line"></i> {property.sqft.toLocaleString()} sqft
          </span>
        </div>

        {/* Agent */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-50">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full overflow-hidden bg-stone-100">
              <img src={property.agent.avatar} alt={property.agent.name} className="w-full h-full object-cover object-top" />
            </div>
            <span className="text-stone-500 text-xs">{property.agent.name}</span>
          </div>
          <div className="flex items-center gap-1 text-amber-500 text-xs">
            <i className="ri-star-fill text-xs"></i>
            <span className="font-semibold">{property.rating}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
